import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Calendar, User, ArrowLeft, ArrowRight, Clock, MessageSquare, Heart,
    Loader2, Star, ThumbsUp, ThumbsDown, Trash2, ChevronLeft,
    ChevronRight, ChevronDown, Sparkles, Terminal, Share2,
    ListFilter, Check, AlertCircle
} from 'lucide-react';
import gsap from 'gsap';
import toast from 'react-hot-toast';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Autoplay, FreeMode, Mousewheel } from 'swiper/modules';
import ProgressiveImage from '../components/common/ProgressiveImage';
import { fetchBlogByIdThunk, fetchBlogsThunk, likeBlogThunk, addCommentThunk, setCurrentBlog } from '../redux/slice/blog.slice';
import { deleteReview, toggleLikeReview, toggleDislikeReview } from '../redux/slice/review.slice';
import DeleteModal from '../components/admin/DeleteModal';
import { Helmet } from 'react-helmet-async';
import useGridCorners, { getCornerStyle } from '../hooks/useGridCorners';

const parseMarkdownBlocks = (content) => {
    if (!content) return { blocks: [], outline: [] };
    const rawLines = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    const blocks = [];
    const outline = [];
    const usedSlugs = {};
    let currentParagraph = [];
    let currentList = [];
    let currentQuote = [];

    const getUniqueSlug = (text) => {
        const clean = (text || '').replace(/<[^>]*>?/gm, '').replace(/[*_~`]/g, '').trim();
        let baseSlug = clean
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
        if (!baseSlug) baseSlug = `section-${outline.length + 1}`;

        if (usedSlugs[baseSlug]) {
            usedSlugs[baseSlug] += 1;
            return `${baseSlug}-${usedSlugs[baseSlug]}`;
        } else {
            usedSlugs[baseSlug] = 1;
            return baseSlug;
        }
    };

    const flushParagraph = () => {
        if (currentParagraph.length > 0) {
            blocks.push({ type: 'paragraph', content: currentParagraph.join('\n').trim() });
            currentParagraph = [];
        }
    };
    const flushList = () => {
        if (currentList.length > 0) {
            blocks.push({ type: 'list', items: [...currentList] });
            currentList = [];
        }
    };
    const flushQuote = () => {
        if (currentQuote.length > 0) {
            blocks.push({ type: 'quote', content: currentQuote.join('\n').trim() });
            currentQuote = [];
        }
    };
    const flushAll = () => {
        flushParagraph();
        flushList();
        flushQuote();
    };

    rawLines.forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed) {
            flushAll();
            return;
        }

        // 1. Check HTML Headings (e.g. <h2>...</h2> or <h3>...</h3>)
        const htmlHeadingMatch = trimmed.match(/^<(h[1-4])[^>]*>(.*?)<\/\1>$/i);
        if (htmlHeadingMatch) {
            flushAll();
            const tag = htmlHeadingMatch[1].toLowerCase();
            const rawText = htmlHeadingMatch[2].replace(/<[^>]*>?/gm, '').trim();
            const cleanLabel = rawText.replace(/[*_~`]/g, '').trim();
            const slugId = getUniqueSlug(cleanLabel);
            outline.push({ id: slugId, label: cleanLabel });
            blocks.push({ type: tag === 'h3' ? 'h3' : 'h2', text: cleanLabel, slugId });
            return;
        }

        // 2. Check Markdown Headings (e.g. # Title, ## Section, ### Sub-section)
        const mdHeadingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
        if (mdHeadingMatch) {
            flushAll();
            const level = mdHeadingMatch[1].length;
            const rawText = mdHeadingMatch[2].trim();
            const cleanLabel = rawText.replace(/<[^>]*>?/gm, '').replace(/[*_~`]/g, '').trim();
            const slugId = getUniqueSlug(cleanLabel);
            outline.push({ id: slugId, label: cleanLabel });
            blocks.push({ type: level >= 3 ? 'h3' : 'h2', text: cleanLabel, slugId });
            return;
        }

        if (trimmed.startsWith('>')) {
            flushParagraph();
            flushList();
            currentQuote.push(trimmed.replace(/^>\s*/, '').replace(/"/g, ''));
        } else if (trimmed.startsWith('* ') || trimmed.startsWith('- ') || trimmed.startsWith('*   ')) {
            flushParagraph();
            flushQuote();
            currentList.push(trimmed.replace(/^(\*|-|\*   )\s*/, ''));
        } else {
            flushList();
            flushQuote();
            currentParagraph.push(line);
        }
    });

    flushAll();
    return { blocks, outline };
};

function BlogDetailSkeleton() {
    return (
        <div className="w-full min-h-screen pb-12 sm:pb-24 relative bg-transparent font-cyber text-gray-300">
            {/* 1. Hero Banner Skeleton */}
            <div className="relative h-[50vh] sm500:h-[55vh] md:h-[60vh] lg:h-[65vh] min-h-[340px] sm:min-h-[400px] md:min-h-[460px] lg:min-h-[520px] max-h-[700px] w-full bg-[#070914] overflow-hidden">
                {/* Top & Bottom Overlay Gradients */}
                <div className="absolute inset-x-0 top-0 h-36 sm:h-48 bg-gradient-to-b from-black/80 via-black/35 to-transparent pointer-events-none z-[1]"></div>
                <div className="absolute inset-x-0 bottom-0 h-52 sm:h-64 bg-gradient-to-t from-[#030305] via-[#030305]/50 to-transparent pointer-events-none"></div>

                <div className="absolute inset-0 flex flex-col justify-end px-4 sm:px-6 lg:px-8 pb-5 sm:pb-8 lg:pb-12 container mx-auto w-full z-10 space-y-3 sm:space-y-4">
                    {/* Back Button Skeleton */}
                    <div className="h-8 sm:h-9 w-32 sm:w-36 bg-white/10 rounded-xl animate-shimmer"></div>

                    <div className="space-y-3 max-w-4xl w-full">
                        {/* Category Row Skeleton */}
                        <div className="flex items-center gap-2">
                            <div className="h-5 sm:h-6 w-20 sm:w-24 bg-cyber-cyan/20 border border-cyber-cyan/30 rounded-full animate-shimmer"></div>
                        </div>

                        {/* Title Skeleton */}
                        <div className="space-y-2 w-full">
                            <div className="h-7 sm:h-9 md:h-11 w-11/12 sm:w-4/5 bg-white/15 rounded-xl animate-shimmer"></div>
                            <div className="h-7 sm:h-9 md:h-11 w-3/4 sm:w-1/2 bg-white/10 rounded-xl animate-shimmer"></div>
                        </div>

                        {/* Meta Info Bar Skeleton */}
                        <div className="flex flex-wrap items-center gap-3 sm:gap-6 pt-1">
                            <div className="h-4 w-28 bg-white/10 rounded-md animate-shimmer"></div>
                            <div className="h-4 w-24 bg-white/10 rounded-md animate-shimmer"></div>
                            <div className="h-4 w-20 bg-white/10 rounded-md animate-shimmer"></div>
                            <div className="h-6 w-14 bg-white/10 rounded-lg animate-shimmer"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Main Content Skeleton */}
            <div className="relative z-10 px-4 sm:px-6 lg:px-8 container mx-auto pt-6 sm:pt-10">
                <div className="space-y-8 sm:space-y-12 w-full">

                    {/* Content Grid (Left Body, Right Outline) */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start pt-1 sm:pt-2 w-full">
                        {/* Left Column: Body Blocks Skeleton */}
                        <div className="lg:col-span-8 space-y-6">
                            {/* Heading */}
                            <div className="h-8 sm:h-9 w-2/5 bg-white/15 rounded-xl animate-shimmer"></div>

                            {/* Paragraph Lines */}
                            <div className="space-y-2.5">
                                <div className="h-4 w-full bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-4 w-11/12 bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-4 w-4/5 bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-4 w-full bg-white/10 rounded animate-shimmer"></div>
                            </div>

                            {/* Blockquote Skeleton */}
                            <div className="p-4 rounded-r-xl border-l-4 border-purple-500/50 bg-white/[0.03] space-y-2">
                                <div className="h-3.5 w-5/6 bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-3.5 w-3/4 bg-white/10 rounded animate-shimmer"></div>
                            </div>

                            {/* Second Heading */}
                            <div className="h-7 sm:h-8 w-1/3 bg-white/15 rounded-xl animate-shimmer pt-2"></div>

                            {/* List items skeleton */}
                            <div className="space-y-3">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-purple-500/60 shrink-0"></div>
                                        <div className="h-4 w-3/4 bg-white/10 rounded animate-shimmer"></div>
                                    </div>
                                ))}
                            </div>

                            {/* More Paragraph Lines */}
                            <div className="space-y-2.5">
                                <div className="h-4 w-full bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-4 w-5/6 bg-white/10 rounded animate-shimmer"></div>
                            </div>

                            {/* Like & Share Action Bar Skeleton */}
                            <div className="flex items-center justify-between pt-4 border-t border-white/10 my-6">
                                <div className="h-5 w-28 bg-white/10 rounded-md animate-shimmer"></div>
                                <div className="h-5 w-20 bg-white/10 rounded-md animate-shimmer"></div>
                            </div>
                        </div>

                        {/* Right Column: Outline Sidebar Skeleton (Desktop Only) */}
                        <div className="lg:col-span-4 sticky top-28 space-y-4 hidden lg:block border-l border-white/10 pl-6 py-2">
                            <div className="h-6 w-28 bg-white/15 rounded-md animate-shimmer border-b border-white/10 pb-2"></div>
                            <div className="space-y-3 pt-2">
                                <div className="h-4 w-4/5 bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-4 w-3/5 bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-4 w-2/3 bg-white/10 rounded animate-shimmer"></div>
                                <div className="h-4 w-1/2 bg-white/10 rounded animate-shimmer"></div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Comments Section Skeleton */}
                    <div className="space-y-5 sm:space-y-6 pt-6 sm:pt-8 w-full border-t border-white/10">
                        {/* Header: Comments Title & Add Review Button */}
                        <div className="flex items-center justify-between gap-3">
                            <div className="h-7 w-36 bg-white/15 rounded-xl animate-shimmer"></div>
                            <div className="h-8 w-28 bg-white/10 rounded-xl animate-shimmer"></div>
                        </div>

                        {/* Comments Container */}
                        <div className="bg-[#0a0c16]/90 border border-white/10 rounded-2xl p-4 sm:p-6 space-y-4">
                            {[1, 2].map((i) => (
                                <div key={i} className="space-y-3 border-b border-white/5 pb-4 last:border-0 last:pb-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-white/10 animate-shimmer shrink-0"></div>
                                            <div className="space-y-1.5">
                                                <div className="h-3.5 w-28 bg-white/15 rounded animate-shimmer"></div>
                                                <div className="h-2.5 w-20 bg-white/10 rounded animate-shimmer"></div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="h-6 w-12 bg-white/10 rounded-lg animate-shimmer"></div>
                                            <div className="h-6 w-12 bg-white/10 rounded-lg animate-shimmer"></div>
                                        </div>
                                    </div>
                                    <div className="sm:pl-12 space-y-1.5">
                                        <div className="h-3.5 w-full bg-white/10 rounded animate-shimmer"></div>
                                        <div className="h-3.5 w-2/3 bg-white/10 rounded animate-shimmer"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 4. Read More Section Skeleton */}
                    <div className="pt-8 sm:pt-12 pb-6 space-y-6 w-full">
                        <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center mb-6 sm:mb-8 space-y-2">
                            <div className="h-4 w-28 bg-cyber-cyan/20 rounded-full animate-shimmer"></div>
                            <div className="h-8 sm:h-10 w-44 bg-white/15 rounded-xl animate-shimmer"></div>
                            <div className="h-3.5 w-72 bg-white/10 rounded animate-shimmer"></div>
                        </div>

                        {/* 4 Cards Responsive Grid Skeleton */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 w-full">
                            {[1, 2, 3, 4].map((i, index) => (
                                <div key={i} style={getCornerStyle(index, 4, 4)} className="bg-[#080814]/95 border border-white/10 overflow-hidden shadow-xl flex flex-col justify-between h-full animate-pulse animate-shimmer">
                                    <div>
                                        {/* Image Banner Skeleton */}
                                        <div className="relative aspect-[16/10] bg-white/5 shrink-0" style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
                                            {/* Top-Left Category Badge Skeleton */}
                                            <div className="absolute top-2.5 left-2.5 h-5 w-16 bg-white/10 rounded-lg border border-white/5"></div>
                                            {/* Read Time Overlay Skeleton */}
                                            <div className="absolute bottom-2.5 right-2.5 h-4 w-12 bg-white/10 rounded-md border border-white/5"></div>
                                        </div>
                                        {/* Card Content Skeleton */}
                                        <div className="p-3.5 sm:p-4 space-y-2">
                                            <div className="h-3 w-20 bg-white/10 rounded"></div>
                                            <div className="h-4.5 w-5/6 bg-white/15 rounded-md"></div>
                                            <div className="h-3 w-full bg-white/10 rounded"></div>
                                        </div>
                                    </div>
                                    {/* Action Footer Skeleton */}
                                    <div className="px-3.5 sm:px-4 pb-3.5 sm:pb-4 pt-2.5 border-t border-white/5 flex items-center justify-between">
                                        <div className="h-3.5 w-24 bg-white/10 rounded"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default function BlogDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const articleRef = useRef(null);
    const animatedIdRef = useRef(null);
    const isManualScrollingRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    const { currentBlog, blogs, loading, error } = useSelector((state) => state.blog);
    const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ defaultCols: 4 });

    // Instant blog resolution: check currentBlog, blogs cache in Redux
    const toBlogSlug = (title) => {
        if (!title || typeof title !== 'string') return '';
        return title
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const isMatchingBlog = (b) => {
        if (!b) return false;
        const generatedSlug = toBlogSlug(b.title);
        return (
            b.slug === id ||
            (generatedSlug && generatedSlug === id) ||
            b._id === id ||
            b.id === id ||
            String(b._id || b.id) === String(id)
        );
    };

    const blogFromList = (blogs || []).find(isMatchingBlog);
    const post = (currentBlog && isMatchingBlog(currentBlog) ? currentBlog : null) || blogFromList;
    const { user: currentUser, isAuthenticated } = useSelector((state) => state.auth);

    const [showReviewForm, setShowReviewForm] = useState(false);
    const [hoverRating, setHoverRating] = useState(0);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [reviewToDelete, setReviewToDelete] = useState(null);
    const [activeSection, setActiveSection] = useState(null);
    const [isMobileOutlineOpen, setIsMobileOutlineOpen] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [commentPage, setCommentPage] = useState(1);
    const commentsPerPage = 4;

    const formik = useFormik({
        initialValues: {
            comment: '',
            rating: 5,
        },
        validationSchema: Yup.object({
            comment: Yup.string()
                .trim('Comment cannot contain only spaces')
                .min(3, 'Comment must be at least 3 characters')
                .max(500, 'Comment cannot exceed 500 characters')
                .required('Please enter your comment'),
            rating: Yup.number()
                .min(1, 'Rating must be at least 1 star')
                .max(5, 'Rating cannot exceed 5 stars')
                .required('Rating is required'),
        }),
        onSubmit: (values) => {
            dispatch(addCommentThunk({
                id: id,
                comment: values.comment.trim(),
                rating: values.rating
            })).unwrap().then(() => {
                formik.resetForm();
                setShowReviewForm(false);
                toast.success("Comment posted successfully!");
                dispatch(fetchBlogByIdThunk(id));
            }).catch((err) => {
                toast.error(err || "Failed to post comment");
            });
        }
    });

    useEffect(() => {
        if (id) {
            if (blogFromList && (!currentBlog || !isMatchingBlog(currentBlog))) {
                dispatch(setCurrentBlog(blogFromList));
            }
            dispatch(fetchBlogByIdThunk(id));
            window.scrollTo(0, 0);
            if (window.__lenis) {
                window.__lenis.scrollTo(0, { immediate: true, force: true });
            }
        }
        if (!blogs || blogs.length === 0) {
            dispatch(fetchBlogsThunk({ limit: 12 }));
        }
    }, [id, dispatch]);

    useEffect(() => {
        if (post && animatedIdRef.current !== post._id) {
            gsap.fromTo(articleRef.current,
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
            );
            animatedIdRef.current = post._id;
        }
    }, [post]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            navigate('/blog');
        }
    }, [error, navigate]);

    // Dynamically extract structured blocks and outline sections from post content
    const { blocks: contentBlocks, outline: outlineSections } = useMemo(() => {
        return parseMarkdownBlocks(post?.content);
    }, [post?.content]);

    // Dynamic Scroll-Spy: Auto highlight active section on scroll with Lenis & native support
    useEffect(() => {
        if (!outlineSections || outlineSections.length === 0) return;

        // Initialize active section to first section if empty
        if (!activeSection) {
            setActiveSection(outlineSections[0]?.id);
        }

        let isThrottled = false;
        const handleScrollSpy = () => {
            // When user clicked an outline link, hold the selected section during smooth scroll
            if (isManualScrollingRef.current) return;

            if (isThrottled) return;
            isThrottled = true;
            requestAnimationFrame(() => {
                isThrottled = false;
                if (isManualScrollingRef.current) return;

                const headerOffset = window.innerWidth < 640 ? 110 : 140;
                const scrollY = (window.__lenis ? window.__lenis.scroll : null) ?? (window.pageYOffset || document.documentElement.scrollTop);
                const windowHeight = window.innerHeight;
                const docHeight = document.documentElement.scrollHeight;

                // If near bottom of page, activate last outline heading
                if (scrollY + windowHeight >= docHeight - 120) {
                    setActiveSection(outlineSections[outlineSections.length - 1].id);
                    return;
                }

                // If at the top of the page, activate first section
                if (scrollY < 200) {
                    setActiveSection(outlineSections[0]?.id);
                    return;
                }

                let currentActive = outlineSections[0]?.id || null;
                const triggerPoint = headerOffset + 60; // Safe trigger point below header

                for (let i = 0; i < outlineSections.length; i++) {
                    const section = outlineSections[i];
                    const el = document.getElementById(section.id);
                    if (el) {
                        const top = el.getBoundingClientRect().top;
                        if (top <= triggerPoint) {
                            currentActive = section.id;
                        }
                    }
                }
                setActiveSection(currentActive);
            });
        };

        const handleUserManualScroll = () => {
            if (isManualScrollingRef.current) {
                isManualScrollingRef.current = null;
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
            }
        };

        window.addEventListener('scroll', handleScrollSpy, { passive: true });
        window.addEventListener('wheel', handleUserManualScroll, { passive: true });
        window.addEventListener('touchstart', handleUserManualScroll, { passive: true });

        if (window.__lenis) {
            window.__lenis.on('scroll', handleScrollSpy);
        }
        handleScrollSpy();

        return () => {
            window.removeEventListener('scroll', handleScrollSpy);
            window.removeEventListener('wheel', handleUserManualScroll);
            window.removeEventListener('touchstart', handleUserManualScroll);
            if (window.__lenis) {
                window.__lenis.off('scroll', handleScrollSpy);
            }
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [outlineSections, post]);

    if (loading && !post) {
        return <BlogDetailSkeleton />;
    }

    if (!post) return null;

    const formatDate = (dateStr) => {
        if (!dateStr) return '16, Jun 2026';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const day = d.getDate();
        const month = d.toLocaleString('en-US', { month: 'short' });
        const year = d.getFullYear();
        return `${day}, ${month} ${year}`;
    };

    const handleLike = async () => {
        if (!isAuthenticated) {
            toast.error("Please login to like this article");
            return;
        }
        try {
            const res = await dispatch(likeBlogThunk(post._id || post.id)).unwrap();
            const nowLiked = (res.likes || []).includes(currentUser?._id);
            toast.success(nowLiked ? "Article liked!" : "Article unliked");
        } catch (err) {
            toast.error(err || "Failed to update like status");
        }
    };

    const handleShare = async () => {
        const shareUrl = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: post.title,
                    text: post.excerpt || post.title,
                    url: shareUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    copyToClipboard(shareUrl);
                }
            }
        } else {
            copyToClipboard(shareUrl);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedLink(true);
            toast.success("Article link copied to clipboard!");
            setTimeout(() => setCopiedLink(false), 2500);
        }).catch(() => {
            toast.error("Failed to copy link");
        });
    };

    const isUserLikedReview = (rev) => {
        if (!currentUser?._id || !rev?.likes) return false;
        return Array.isArray(rev.likes) && rev.likes.some(id => (typeof id === 'object' ? id._id === currentUser._id : id === currentUser._id));
    };

    const isUserDislikedReview = (rev) => {
        if (!currentUser?._id || !rev?.dislikes) return false;
        return Array.isArray(rev.dislikes) && rev.dislikes.some(id => (typeof id === 'object' ? id._id === currentUser._id : id === currentUser._id));
    };

    const handleLikeReview = (reviewId) => {
        if (!isAuthenticated) {
            toast.error("Please login to like comments");
            return;
        }
        dispatch(toggleLikeReview(reviewId))
            .unwrap()
            .then((res) => {
                const isNowLiked = (res.likes || []).some(id => (typeof id === 'object' ? id._id === currentUser?._id : id === currentUser?._id));
                toast.success(isNowLiked ? "Liked comment" : "Like removed");
            })
            .catch((err) => {
                toast.error(err || "Failed to update like status");
            });
    };

    const handleDislikeReview = (reviewId) => {
        if (!isAuthenticated) {
            toast.error("Please login to dislike comments");
            return;
        }
        dispatch(toggleDislikeReview(reviewId))
            .unwrap()
            .then((res) => {
                const isNowDisliked = (res.dislikes || []).some(id => (typeof id === 'object' ? id._id === currentUser?._id : id === currentUser?._id));
                toast.success(isNowDisliked ? "Disliked comment" : "Dislike removed");
            })
            .catch((err) => {
                toast.error(err || "Failed to update dislike status");
            });
    };

    const handleDeleteReview = (reviewId) => {
        dispatch(deleteReview(reviewId)).then(() => {
            toast.success("Comment deleted successfully!");
            setIsDeleteModalOpen(false);
            setReviewToDelete(null);
        });
    };

    // Smooth scroll helper with header clearance offset so clicked headings are never hidden under header
    const scrollToSection = (e, sectionId) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const element = document.getElementById(sectionId);
        if (!element) return;

        setActiveSection(sectionId);
        isManualScrollingRef.current = sectionId;

        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            isManualScrollingRef.current = null;
        }, 1100);

        // Header offset clearance (110px mobile / 140px desktop)
        const headerOffset = window.innerWidth < 640 ? 110 : 140;

        if (window.__lenis) {
            window.__lenis.scrollTo(element, {
                offset: -headerOffset,
                duration: 0.8,
                immediate: false,
                lock: false,
                onComplete: () => {
                    isManualScrollingRef.current = null;
                    setActiveSection(sectionId);
                }
            });
        } else {
            const elementPosition = element.getBoundingClientRect().top;
            const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
            const offsetPosition = elementPosition + currentScroll - headerOffset;
            window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth'
            });
        }

        try {
            window.history.replaceState(null, '', `#${sectionId}`);
        } catch (_) { }
    };

    const isLiked = (post.likes || []).includes(currentUser?._id);
    const activeReviews = (post?.reviews || []).filter(rev => rev.status === 'Published' || rev.user?._id === currentUser?._id || currentUser?.role === 'admin');

    // Pagination for comments (Left / Right Arrow Only)
    const totalCommentPages = Math.ceil(activeReviews.length / commentsPerPage) || 1;
    const currentReviews = activeReviews.slice((commentPage - 1) * commentsPerPage, commentPage * commentsPerPage);

    // Dynamic related blogs list excluding current post for READ MORE carousel
    const relatedBlogs = (blogs || []).filter(b => (b._id || b.id) !== (post._id || post.id));

    return (
        <div className="w-full min-h-screen pb-12 sm:pb-24 relative bg-transparent font-cyber text-gray-300 selection:bg-cyber-blue/30 selection:text-white">
            <Helmet>
                <title>{`${post.title} | CUBE GAMES Blog`}</title>
                <meta name="description" content={post.excerpt || (post.content ? post.content.slice(0, 160) : "")} />
                <meta name="keywords" content={`${post.title}, ${post.category || 'gaming'}, gaming news, esports update`} />
                <link rel="canonical" href={`https://cubegames.ca/blog/${id}`} />

                {/* Open Graph */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={`${post.title} | CUBE GAMES Blog`} />
                <meta property="og:description" content={post.excerpt || (post.content ? post.content.slice(0, 160) : "")} />
                <meta property="og:image" content={post.image?.url || post.image} />
                <meta property="og:url" content={`https://cubegames.ca/blog/${id}`} />
            </Helmet>

            {/* ─ Top Hero Banner Section (Balanced cinematic overlay) ─ */}
            <div className="relative h-[50vh] sm500:h-[55vh] md:h-[60vh] lg:h-[65vh] min-h-[340px] sm:min-h-[400px] md:min-h-[460px] lg:min-h-[520px] max-h-[700px] w-full overflow-hidden">
                <div className="absolute inset-0">
                    <ProgressiveImage
                        src={post.image?.url || post.image}
                        placeholderSrc={post.image?.lowUrl || post.imageLow}
                        alt={post.title}
                        className="w-full h-full object-cover object-center"
                    />
                    {/* Top gradient for crystal clear navbar visibility */}
                    <div className="absolute inset-x-0 top-0 h-36 sm:h-48 bg-gradient-to-b from-black/80 via-black/35 to-transparent pointer-events-none z-[1]"></div>
                    {/* Moderate cinematic shadow overlay for perfect contrast */}
                    <div className="absolute inset-0 bg-black/25 pointer-events-none"></div>
                    {/* Bottom gradient fade so title text and metadata stand out */}
                    <div className="absolute inset-x-0 bottom-0 h-52 sm:h-64 bg-gradient-to-t from-[#030305] via-[#030305]/50 to-transparent pointer-events-none"></div>
                </div>

                <div className="absolute inset-0 flex flex-col justify-end px-4 sm:px-6 lg:px-8 pb-5 sm:pb-8 lg:pb-12 container mx-auto w-full z-10">
                    {/* Back to Blog Button */}
                    <button
                        onClick={() => navigate('/blog')}
                        className="group inline-flex items-center gap-2 mb-2 sm:mb-3.5 w-fit px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-[#0a0c16]/85 hover:bg-[#00f0ff]/10 border border-white/10 hover:border-[#00f0ff]/60 text-gray-300 hover:text-white text-[10px] sm:text-xs font-cyber font-bold tracking-widest uppercase transition-all duration-300 backdrop-blur-md shadow-lg hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] active:scale-95"
                    >
                        <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#00f0ff] transition-transform duration-300 group-hover:-translate-x-1 shrink-0" />
                        <span>BACK TO BLOG</span>
                    </button>

                    <div className="space-y-2 sm:space-y-3 max-w-4xl">
                        {/* Category Badge */}
                        <div className="flex items-center gap-2">
                            <span className="px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full bg-cyber-cyan/20 border border-cyber-cyan/50 text-cyber-cyan text-[10px] sm:text-xs font-cyber font-bold tracking-wider uppercase backdrop-blur-md shadow-[0_0_15px_rgba(0,240,255,0.2)]">
                                {post.category || 'GAMES'}
                            </span>
                        </div>

                        {/* Article Title */}
                        <h1 className="text-xl sm375:text-2xl sm500:text-3xl md:text-4xl lg:text-5xl font-cyber font-black tracking-wide sm:tracking-wider leading-tight uppercase text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)] break-words">
                            {post.title}
                        </h1>

                        {/* Metadata & Actions Bar */}
                        <div className="flex flex-wrap items-center gap-2.5 sm:gap-4 md:gap-6 text-[10px] sm375:text-[11px] sm:text-xs text-gray-300 pt-1 font-cyber">
                            <div className="flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5 text-[#00f0ff] shrink-0" />
                                <span className="text-gray-200 font-semibold truncate max-w-[120px] sm:max-w-none">{post.author?.name || post.authorName || 'Cube Staff'}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-[#00f0ff] shrink-0" />
                                <span>{formatDate(post.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-[#00f0ff] shrink-0" />
                                <span>{Math.ceil((post.content?.length || 1000) / 300)} min read</span>
                            </div>
                            <button
                                onClick={handleLike}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-lg border transition-all ${isLiked ? 'bg-cyber-pink/20 border-cyber-pink text-cyber-pink shadow-[0_0_12px_rgba(255,0,122,0.4)]' : 'bg-white/5 border-white/10 hover:border-cyber-pink/50 text-gray-300'}`}
                            >
                                <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-cyber-pink text-cyber-pink' : 'text-cyber-pink'}`} />
                                <span className="font-mono text-xs">{post.likes?.length || 0}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─ Main Content Container ─ */}
            <div className="relative z-10 px-4 sm:px-6 lg:px-8 container mx-auto pt-6 sm:pt-10">
                <div ref={articleRef} className="space-y-8 sm:space-y-12 w-full">

                    {/* ─ Main Content Grid (Left: Body Content | Right: Table of Contents Outline on Desktop Only) ─ */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start pt-1 sm:pt-2 w-full">

                        {/* Left Column: Article Text Content */}
                        <div className={`${outlineSections.length > 0 ? 'lg:col-span-8' : 'lg:col-span-12'} space-y-5 sm:space-y-6 text-gray-300 text-xs sm:text-sm md:text-base leading-relaxed overflow-hidden break-words`}>

                            {/* Mobile / Tablet Table of Contents Accordion */}
                            {/* {outlineSections.length > 0 && (
                                <div className="lg:hidden mb-6 p-3.5 sm:p-4 rounded-xl bg-[#080a14]/90 border border-white/10 backdrop-blur-md">
                                    <button
                                        type="button"
                                        onClick={() => setIsMobileOutlineOpen(!isMobileOutlineOpen)}
                                        className="flex items-center justify-between w-full text-xs font-cyber font-bold text-white tracking-wider uppercase cursor-pointer"
                                    >
                                        <span className="flex items-center gap-2">
                                            <ListFilter className="w-4 h-4 text-[#00f0ff]" />
                                            <span>TABLE OF CONTENTS ({outlineSections.length})</span>
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-[#00f0ff] transition-transform duration-300 ${isMobileOutlineOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isMobileOutlineOpen && (
                                        <nav className="mt-3 pt-3 border-t border-white/10 space-y-2">
                                            {outlineSections.map((sec) => (
                                                <a
                                                    key={sec.id}
                                                    href={`#${sec.id}`}
                                                    onClick={(e) => {
                                                        scrollToSection(e, sec.id);
                                                        setIsMobileOutlineOpen(false);
                                                    }}
                                                    className={`block py-1.5 text-xs transition-colors ${activeSection === sec.id ? 'text-[#00f0ff] font-semibold pl-2 border-l-2 border-[#00f0ff]' : 'text-gray-400 hover:text-white'}`}
                                                >
                                                    {sec.label}
                                                </a>
                                            ))}
                                        </nav>
                                    )}
                                </div>
                            )} */}

                            {/* Main Body Paragraphs & Headings with exact matching IDs */}
                            {contentBlocks.length > 0 ? (
                                contentBlocks.map((block, index) => {
                                    if (block.type === 'h3') {
                                        return (
                                            <h3 key={index} id={block.slugId} className="text-base sm:text-xl md:text-2xl text-white pt-3 uppercase tracking-wider flex items-center gap-2 break-words scroll-mt-32 sm:scroll-mt-36">
                                                <Terminal className="w-4 h-4 text-[#00f0ff] shrink-0" />
                                                <span>{block.text}</span>
                                            </h3>
                                        );
                                    }
                                    if (block.type === 'h2') {
                                        return (
                                            <h2 key={index} id={block.slugId} className="text-lg sm:text-2xl md:text-3xl text-white pt-4 tracking-wider break-words scroll-mt-32 sm:scroll-mt-36">
                                                {block.text}
                                            </h2>
                                        );
                                    }
                                    if (block.type === 'quote') {
                                        return (
                                            <blockquote key={index} className="pl-3.5 sm:pl-4 border-l-4 border-purple-500 bg-white/[0.02] p-3 sm:p-4 rounded-r-xl italic text-white/90 text-xs sm:text-sm break-words">
                                                {block.content}
                                            </blockquote>
                                        );
                                    }
                                    if (block.type === 'list') {
                                        return (
                                            <ul key={index} className="space-y-2.5 pt-2">
                                                {block.items.map((li, i) => (
                                                    <li key={i} className="flex items-start gap-2.5">
                                                        <span className="w-2 h-2 rounded-full bg-purple-500 shrink-0 mt-1.5"></span>
                                                        <div className="text-xs sm:text-sm md:text-base break-words flex-1">
                                                            {li.includes(':') ? (
                                                                <>
                                                                    <strong className="text-white font-semibold">{li.split(':')[0].replace(/[*|-]/g, '').trim()}:</strong>
                                                                    {li.split(':').slice(1).join(':')}
                                                                </>
                                                            ) : (
                                                                <span>{li.replace(/[*|-]/g, '').trim()}</span>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        );
                                    }
                                    return <p key={index} className="whitespace-pre-line leading-relaxed text-gray-300 text-xs sm:text-sm md:text-base break-words">{block.content}</p>;
                                })
                            ) : (
                                <p className="text-gray-400 text-xs sm:text-sm">{post.excerpt || post.description || 'No article content available.'}</p>
                            )}

                            {/* ─ Like & Share Action Buttons Bar ─ */}
                            <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-6 pt-4 border-t border-[#1e2338] pb-4 my-6">
                                <button
                                    onClick={handleLike}
                                    className={`inline-flex items-center gap-1.5 text-xs tracking-wider uppercase transition-colors ${isLiked ? 'text-pink-500' : 'text-gray-300 hover:text-pink-500'}`}
                                >
                                    <Heart className={`w-4 h-4 ${isLiked ? 'fill-pink-500 text-pink-500' : 'text-[#00f0ff]'}`} />
                                    <span>{isLiked ? 'LIKED' : 'Like Article'} ({post.likes?.length || 0})</span>
                                </button>

                                <button
                                    onClick={handleShare}
                                    className="inline-flex items-center gap-1.5 text-xs tracking-wider uppercase text-gray-400 hover:text-[#00f0ff] transition-colors ml-auto sm:ml-0"
                                >
                                    {copiedLink ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Share2 className="w-3.5 h-3.5 text-[#00f0ff]" />}
                                    <span>{copiedLink ? 'Copied' : 'Share'}</span>
                                </button>
                            </div>

                        </div>

                        {/* Right Column (4 Cols): Table of Contents Outline Sidebar for Desktop */}
                        {outlineSections.length > 0 && (
                            <div className="lg:col-span-4 sticky top-28 space-y-4 hidden lg:block border-l border-white/10 pl-6 py-2 self-start">
                                <h4 className="text-lg text-white tracking-wider border-b border-white/10 pb-2 flex items-center gap-2">
                                    <ListFilter className="w-4 h-4 text-[#00f0ff]" />
                                    <span>OUTLINE</span>
                                </h4>
                                <nav className="space-y-3 text-xs">
                                    {outlineSections.map((sec) => (
                                        <a
                                            key={sec.id}
                                            href={`#${sec.id}`}
                                            onClick={(e) => scrollToSection(e, sec.id)}
                                            className={`block transition-all duration-200 ${activeSection === sec.id
                                                ? 'text-[#00f0ff] font-semibold border-l-2 border-[#00f0ff] -ml-[25px] pl-5'
                                                : 'text-gray-400 hover:text-white'
                                                }`}
                                        >
                                            {sec.label}
                                        </a>
                                    ))}
                                </nav>
                            </div>
                        )}

                    </div>

                    {/* ─ COMMENTS SECTION (Only Comments with Add Review Button on Right) ─ */}
                    <div className="space-y-5 sm:space-y-6 pt-6 sm:pt-8 w-full border-t border-white/10">
                        {/* Comments Header: Title on Left, Add Review on Right */}
                        <div className="flex flex-col sm375:flex-row sm375:items-center justify-between gap-3">
                            <h3 className="text-lg sm:text-xl md:text-2xl text-white tracking-wider font-cyber font-bold uppercase flex items-center gap-2">
                                <span>COMMENTS</span>
                                <span className="text-[#00f0ff] text-xs sm:text-sm font-normal">({activeReviews.length})</span>
                            </h3>

                            {/* ADD REVIEW Button on Right (Visible only when logged in) */}
                            {isAuthenticated && (
                                <button
                                    onClick={() => setShowReviewForm(!showReviewForm)}
                                    className="inline-flex items-center justify-center gap-2 px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-[#0b0d19] border border-white/10 hover:border-cyber-cyan text-white text-xs font-cyber tracking-wider uppercase whitespace-nowrap shrink-0 transition-all shadow-md active:scale-95 group w-fit"
                                >
                                    <MessageSquare className="w-3.5 h-3.5 text-cyber-cyan group-hover:scale-110 transition-transform" />
                                    <span>{showReviewForm ? 'CANCEL' : 'ADD REVIEW'}</span>
                                </button>
                            )}
                        </div>

                        {/* Review Form Input Modal */}
                        {isAuthenticated && showReviewForm && (
                            <form onSubmit={formik.handleSubmit} className="p-4 sm:p-6 rounded-2xl bg-[#0a0c16]/95 backdrop-blur-xl border border-white/10 space-y-4 animate-in fade-in duration-300 w-full shadow-2xl">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="space-y-0.5">
                                        <h4 className="text-sm sm:text-base md:text-lg text-white tracking-wider font-cyber">Share Your Thoughts</h4>
                                        <p className="text-[11px] sm:text-xs text-gray-400">Rate and review this article</p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <div className="flex items-center gap-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() => formik.setFieldValue('rating', star)}
                                                    onMouseEnter={() => setHoverRating(star)}
                                                    onMouseLeave={() => setHoverRating(0)}
                                                    className="p-0.5 hover:scale-110 transition-transform"
                                                    title={`Rate ${star} star${star > 1 ? 's' : ''}`}
                                                >
                                                    <Star className={`w-4 h-4 sm:w-5 sm:h-5 ${star <= (hoverRating || formik.values.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-700'}`} />
                                                </button>
                                            ))}
                                        </div>
                                        {formik.touched.rating && formik.errors.rating && (
                                            <span className="text-[10px] text-rose-400 font-cyber">{formik.errors.rating}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <textarea
                                        name="comment"
                                        value={formik.values.comment}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Write your review or thoughts here (min 3 characters)..."
                                        maxLength={500}
                                        className={`w-full bg-[#05060f] border rounded-xl p-3 sm:p-4 text-xs sm:text-sm text-white placeholder-gray-500 focus:outline-none min-h-[95px] resize-none transition-colors ${formik.touched.comment && formik.errors.comment
                                                ? 'border-rose-500/80 focus:border-rose-500'
                                                : 'border-white/10 focus:border-[#00f0ff]'
                                            }`}
                                    />
                                    <div className="flex items-center justify-between gap-2 px-1">
                                        {formik.touched.comment && formik.errors.comment ? (
                                            <span className="text-[11px] text-rose-400 font-cyber flex items-center gap-1 animate-in fade-in">
                                                <AlertCircle className="w-3 h-3 shrink-0" />
                                                <span>{formik.errors.comment}</span>
                                            </span>
                                        ) : (
                                            <span className="text-[10px] text-gray-500 font-cyber">Minimum 3 characters</span>
                                        )}
                                        <span className={`text-[10px] font-mono ml-auto ${formik.values.comment.length >= 480 ? 'text-amber-400' : 'text-gray-500'}`}>
                                            {formik.values.comment.length}/500
                                        </span>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 sm:gap-3 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            formik.resetForm();
                                            setShowReviewForm(false);
                                        }}
                                        className="px-3.5 py-1.5 rounded-lg bg-white/5 text-xs text-gray-400 hover:text-white transition-colors font-cyber"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={formik.isSubmitting}
                                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 disabled:opacity-50 text-white text-xs font-cyber tracking-wider shadow-md transition-all active:scale-95"
                                    >
                                        {formik.isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                        <span>{formik.isSubmitting ? 'Posting...' : 'Submit Review'}</span>
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Comments List Container */}
                        <div className="bg-[#0a0c16]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 space-y-4 sm:space-y-5 shadow-2xl w-full">
                            {currentReviews.length > 0 ? (
                                currentReviews.map((rev) => {
                                    const revId = rev._id || rev.id;
                                    const userName = rev.name || (rev.user?.firstName ? `${rev.user.firstName} ${rev.user.lastName || ''}` : 'VERIFIED GAMER');
                                    const revDate = rev.date || formatDate(rev.createdAt);

                                    return (
                                        <div key={revId} className="space-y-2.5 border-b border-white/5 pb-4 sm:pb-5 last:border-0 last:pb-0 w-full">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    {/* User Avatar */}
                                                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-tr from-purple-500 to-cyan-500 p-[1px] shrink-0">
                                                        <div className="w-full h-full rounded-full bg-[#05060f] flex items-center justify-center text-xs sm:text-sm text-[#00f0ff] font-bold">
                                                            {userName ? userName[0] : 'U'}
                                                        </div>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="text-xs sm:text-sm text-white uppercase tracking-wider truncate font-cyber font-bold">
                                                            {userName}
                                                        </h4>
                                                        <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500">
                                                            <span>{revDate}</span>
                                                            <div className="flex items-center gap-0.5">
                                                                {[1, 2, 3, 4, 5].map((s) => (
                                                                    <Star key={s} className={`w-2.5 h-2.5 ${s <= (rev.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-gray-700'}`} />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Upvote / Downvote & Delete Buttons */}
                                                <div className="flex items-center gap-2 text-xs font-mono shrink-0 ml-auto sm375:ml-0">
                                                    <button
                                                        onClick={() => handleLikeReview(revId)}
                                                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-all text-xs active:scale-95 ${isUserLikedReview(rev)
                                                                ? 'bg-cyber-cyan/20 border-cyber-cyan text-cyber-cyan shadow-[0_0_12px_rgba(0,240,255,0.3)]'
                                                                : 'bg-[#121526]/80 border-white/10 hover:border-cyber-cyan/50 text-gray-400 hover:text-white'
                                                            }`}
                                                        title="Like comment"
                                                    >
                                                        <ThumbsUp className={`w-3.5 h-3.5 ${isUserLikedReview(rev) ? 'fill-cyber-cyan text-cyber-cyan' : 'text-gray-400'}`} />
                                                        <span className="font-bold">{rev.likes?.length || (typeof rev.likes === 'number' ? rev.likes : 0)}</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDislikeReview(revId)}
                                                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-all text-xs active:scale-95 ${isUserDislikedReview(rev)
                                                                ? 'bg-cyber-pink/20 border-cyber-pink text-cyber-pink shadow-[0_0_12px_rgba(255,0,122,0.3)]'
                                                                : 'bg-[#121526]/80 border-white/10 hover:border-cyber-pink/50 text-gray-400 hover:text-white'
                                                            }`}
                                                        title="Dislike comment"
                                                    >
                                                        <ThumbsDown className={`w-3.5 h-3.5 ${isUserDislikedReview(rev) ? 'fill-cyber-pink text-cyber-pink' : 'text-gray-400'}`} />
                                                        <span className="font-bold">{rev.dislikes?.length || (typeof rev.dislikes === 'number' ? rev.dislikes : 0)}</span>
                                                    </button>

                                                    {(currentUser?._id === (rev.user?._id || rev.user) || currentUser?.role === 'admin') && (
                                                        <button
                                                            onClick={() => {
                                                                setReviewToDelete(revId);
                                                                setIsDeleteModalOpen(true);
                                                            }}
                                                            className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-red-500/50 text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all active:scale-95 ml-0.5"
                                                            title="Delete comment"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed sm:pl-11 pl-0 break-words font-sans">
                                                {rev.comment}
                                            </p>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-10 sm:py-12 px-4 border border-dashed border-white/10 rounded-2xl bg-white/[0.01] flex flex-col items-center justify-center space-y-3">
                                    <div className="w-12 h-12 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-gray-500">
                                        <MessageSquare className="w-6 h-6 text-gray-500 opacity-60" />
                                    </div>
                                    <div className="space-y-1 max-w-sm">
                                        <p className="text-xs sm:text-sm font-cyber font-bold uppercase tracking-widest text-gray-300">
                                            NO COMMENTS YET
                                        </p>
                                        <p className="text-[11px] sm:text-xs text-gray-500 font-cyber tracking-wider">
                                            Be the first to share your thoughts!
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Bottom Pagination Bar with Right / Left Arrows */}
                            {totalCommentPages > 1 && (
                                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                                    <span className="text-[11px] sm:text-xs font-cyber text-gray-400">
                                        Showing Page <span className="text-[#00f0ff] font-bold">{commentPage}</span> of <span className="text-white font-bold">{totalCommentPages}</span>
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCommentPage(prev => Math.max(1, prev - 1))}
                                            disabled={commentPage === 1}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0c0e1a] border border-white/10 hover:border-[#00f0ff] text-white disabled:opacity-30 disabled:pointer-events-none transition-all text-xs font-cyber active:scale-95"
                                        >
                                            <ChevronLeft className="w-3.5 h-3.5" />
                                            <span className="hidden sm:inline">PREV</span>
                                        </button>
                                        <button
                                            onClick={() => setCommentPage(prev => Math.min(totalCommentPages, prev + 1))}
                                            disabled={commentPage >= totalCommentPages}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0c0e1a] border border-white/10 hover:border-[#00f0ff] text-white disabled:opacity-30 disabled:pointer-events-none transition-all text-xs font-cyber active:scale-95"
                                        >
                                            <span className="hidden sm:inline">NEXT</span>
                                            <ChevronRight className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ─ "READ MORE" AUTO-SCROLL CAROUSEL SECTION (No Arrows, Continuous Autoplay) ─ */}
                    {relatedBlogs.length > 0 && (
                        <div className="pt-8 sm:pt-12 pb-6 space-y-6 w-full">
                            {/* Title & Subtitle */}
                            <div className="w-full max-w-4xl mx-auto flex flex-col items-center justify-center text-center mb-6 sm:mb-8">
                                <p className="text-[10px] sm:text-xs font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2 justify-center">
                                    <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                                    News & Updates
                                </p>
                                <h2 className="font-cyber font-semibold text-2xl sm375:text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight leading-tight">
                                    READ <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)] inline-block">MORE</span>
                                </h2>
                                <div className="h-[2px] w-16 sm:w-20 animate-gradient-bg mt-3 rounded-full mx-auto"></div>
                                <p className="text-gray-400 font-sans max-w-lg text-xs sm:text-sm mx-auto mt-3.5 leading-relaxed px-2">
                                    Explore related stories, industry insights, and latest gaming updates.
                                </p>
                            </div>

                            {/* Auto-Scroll Swiper Carousel (4 Cards on Desktop, No left/right arrows) */}
                            <div ref={setGridRef} className="relative w-full overflow-hidden">
                                <Swiper
                                    modules={[Autoplay, FreeMode, Mousewheel]}
                                    autoplay={{
                                        delay: 3000,
                                        disableOnInteraction: false,
                                        pauseOnMouseEnter: true
                                    }}
                                    loop={relatedBlogs.length > 4}
                                    speed={800}
                                    mousewheel={{ forceToAxis: true }}
                                    freeMode={false}
                                    spaceBetween={16}
                                    slidesPerView={1}
                                    breakpoints={{
                                        0: { slidesPerView: 1, spaceBetween: 16 },
                                        480: { slidesPerView: 2, spaceBetween: 16 },
                                        768: { slidesPerView: 3, spaceBetween: 18 },
                                        1024: { slidesPerView: 4, spaceBetween: 20 }
                                    }}
                                    className="w-full py-2 sm:py-3"
                                >
                                    {relatedBlogs.map((rel, index) => {
                                        const readTime = Math.max(2, Math.ceil((rel.content?.length || 1000) / 300));
                                        return (
                                            <SwiperSlide key={rel._id || rel.id} className="!h-auto flex">
                                                <div
                                                    onClick={() => {
                                                        dispatch(setCurrentBlog(rel));
                                                        navigate(`/blog/${rel._id || rel.id}`);
                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    }}
                                                    style={getCornerStyle(index, relatedBlogs.length, gridCols)}
                                                    className="group relative bg-[#080814]/95 backdrop-blur-xl border border-white/10 hover:border-cyber-cyan/50 overflow-hidden shadow-xl hover:shadow-[0_0_25px_rgba(0,240,255,0.18)] transition-all duration-300 cursor-pointer flex flex-col justify-between w-full h-full min-w-0"
                                                >
                                                    <div>
                                                        {/* Card Image Banner */}
                                                        <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#05060f] shrink-0" style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
                                                            <img
                                                                src={rel.image?.url || rel.image}
                                                                alt={rel.title}
                                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                                                            />
                                                            <div className="absolute inset-0 bg-gradient-to-t from-[#080814] via-transparent to-transparent opacity-75 pointer-events-none" />

                                                            {/* Top-Left Category Badge */}
                                                            <div className="absolute top-2.5 left-2.5 z-10">
                                                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 text-[10px] font-cyber font-bold tracking-wider text-cyber-cyan uppercase shadow-md group-hover:border-cyber-cyan/50 group-hover:shadow-[0_0_10px_rgba(0,240,255,0.3)] transition-all">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse shrink-0"></span>
                                                                    <span className="truncate max-w-[100px]">{rel.category || 'GAMES'}</span>
                                                                </span>
                                                            </div>

                                                            {/* Read Time Overlay Pill */}
                                                            <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-md border border-white/10 text-[10px] font-mono text-gray-300 font-semibold shadow-md">
                                                                <Clock className="w-3 h-3 text-purple-400" />
                                                                <span>{readTime}m read</span>
                                                            </div>
                                                        </div>

                                                        {/* Card Content */}
                                                        <div className="p-3.5 sm:p-4 space-y-2 min-w-0">
                                                            <div className="flex items-center justify-between text-[10px] text-gray-400 font-mono">
                                                                <span className="flex items-center gap-1.5 text-gray-400">
                                                                    <Calendar className="w-3 h-3 text-[#00f0ff]" />
                                                                    {formatDate(rel.createdAt)}
                                                                </span>
                                                            </div>
                                                            <h3 className="text-xs sm:text-sm font-cyber font-bold text-white group-hover:text-[#00f0ff] transition-colors leading-snug line-clamp-1 truncate uppercase tracking-tight" title={rel.title}>
                                                                {rel.title}
                                                            </h3>
                                                            <p className="text-[11px] text-gray-400 font-sans line-clamp-1 truncate leading-relaxed">
                                                                {rel.excerpt || rel.description || (rel.content ? rel.content.replace(/<[^>]*>?/gm, '').slice(0, 100) : `Explore ${rel.title}`)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Bottom Action Footer */}
                                                    <div className="px-3.5 sm:px-4 pb-3.5 sm:pb-4 pt-2.5 border-t border-white/5 flex items-center justify-between text-xs text-[#00f0ff] font-cyber font-semibold tracking-wider">
                                                        <span className="group-hover:translate-x-1.5 transition-transform inline-flex items-center gap-1.5">
                                                            READ ARTICLE <ArrowRight className="w-3.5 h-3.5" />
                                                        </span>
                                                    </div>
                                                </div>
                                            </SwiperSlide>
                                        );
                                    })}
                                </Swiper>
                            </div>
                        </div>
                    )}

                </div>
            </div>
            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => { setIsDeleteModalOpen(false); setReviewToDelete(null); }}
                onConfirm={() => handleDeleteReview(reviewToDelete)}
            />
        </div>
    );
}
